'use client';

/**
 * NotificationBadge — sidebar badge with unread count + app icon badge.
 *
 * Spec ref: SPEC-146 §P-264 (Sprint 70, 2026-09-07) — F-001 Notificaciones
 *           In-App. ADR-043 (decisión).
 * Track C #2 (Sprint 134): Badge API — navigator.setAppBadge() para
 *           mostrar contador en el icono de la app (como apps nativas).
 *
 * Renders a small badge with the unreadCount next to the "Notificaciones"
 * sidebar item. The badge is fetched via the useNotifications hook in
 * "countOnly" mode (lightweight — only fetches unreadCount, polls every
 * 30s, listens to WebSocket `notification:new` events).
 *
 * Track C #2: also sets the app icon badge via navigator.setAppBadge()
 * when unreadCount > 0, and clears it when 0. The App Badging API is
 * supported in Chrome 81+ (Android + Windows), Edge 81+. Not supported
 * in Firefox/Safari (graceful no-op).
 *
 * Sprint 135 (2026-09-22, Founder directive): added the `variant` prop
 * so the SAME component can render a small dot on the "Cuenta" PARENT
 * group label (WhatsApp-style). The dot is shown when the group is
 * COLLAPSED — without it the user has no visual indicator that there
 * are unread notifications (the count badge is hidden inside the
 * collapsed `Collapsible`).
 *
 *   variant='count' (default) — small red badge with the unread count,
 *                               shown next to the "Notificaciones" item.
 *   variant='dot'              — small solid red dot (no number), shown
 *                               next to the "Cuenta" group label header.
 *
 * Mounting multiple instances (count + dot) is safe — the
 * `setAppBadge()` side-effect is idempotent (sets the same number) and
 * the `useNotifications({ countOnly: true })` polling interval (30s)
 * is acceptable as a lightweight read.
 *
 * Anti-§6: pure UI component — no business logic.
 * Anti-P-041: 0 `as any` casts.
 * Anti-P-042: 0 `console.*` calls.
 */

import { useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { useNotifications } from '@/hooks/use-notifications';

export interface NotificationBadgeProps {
  /**
   * 'count' (default) — renders a small red badge with the unread count.
   * 'dot' — renders a small solid red dot (no number). Used on the
   * "Cuenta" PARENT group label so the user sees a visual indicator
   * without expanding the group (WhatsApp-style).
   */
  variant?: 'count' | 'dot';
}

export function NotificationBadge({ variant = 'count' }: NotificationBadgeProps) {
  // countOnly mode: only fetch unreadCount, poll every 30s, listen to WS
  const { unreadCount } = useNotifications({ countOnly: true });

  // Track C #2: App Badging API — set/clear the app icon badge.
  // navigator.setAppBadge(n) shows a number on the app icon (Android home
  // screen, Windows taskbar). navigator.clearAppBadge() removes it.
  // Graceful no-op on unsupported browsers (Firefox/Safari).
  // Idempotent: safe to call from multiple mounted instances (count + dot).
  useEffect(() => {
    if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
      if (unreadCount > 0) {
        navigator.setAppBadge(unreadCount).catch(() => {
          // Badge API can fail if the document is not focused or the
          // app is not installed. Silently ignore — the in-app badge
          // still works.
        });
      } else {
        navigator.clearAppBadge().catch(() => {
          // Same — silently ignore.
        });
      }
    }
  }, [unreadCount]);

  // Don't render the badge if unreadCount is 0 (clean UI when no new notifs)
  if (unreadCount === 0) return null;

  if (variant === 'dot') {
    // Small solid red dot — used on the "Cuenta" PARENT group label so
    // the user sees an indicator WITHOUT expanding the group. The dot
    // is intentionally compact (8px) so it doesn't crowd the uppercase
    // group label. No `ml-auto` — the parent (CollapsibleTrigger) places
    // it inline next to the label text.
    return (
      <span
        aria-label={`${unreadCount} notificaciones sin leer`}
        className="inline-block size-2 shrink-0 rounded-full bg-destructive"
      />
    );
  }

  return (
    <Badge
      variant="destructive"
      className="ml-auto h-5 px-1.5 text-xs min-w-5 flex items-center justify-center"
      aria-label={`${unreadCount} notificaciones sin leer`}
    >
      {unreadCount > 99 ? '99+' : unreadCount}
    </Badge>
  );
}
